import axios from "axios";
import moment from "moment-timezone";
import { setLocale } from "yup";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { CircleCommonProvider } from "@maysoft/circle-common-react";
import { CommonComponentProvider, ICreateUserSetting, IOrganizationUserProfile, IUserProfile, RoleHelpers, RoleLevel, RoleLevelNormal, RoleType } from "@maysoft/common-component-react";

import Helpers from "commons/helpers";
import Constants, { USER_ROLE } from "constants/index";
import Screens from "constants/screens";
import Strings from "constants/strings";
import NotFoundScreen from "screens/notFound";
import DashboardScreen from "screens/dashboard";
import ProtectedRoute from "routes/protectedRoute";
import useDataCommon from "hooks/useDataCommon.hook";
import LoginRedirectScreen from "screens/login/loginRedirect";
import ZaloBrowserRedirect from "components/ZaloBrowserRedirect";

import { RootState } from "store";
import { MainLayout } from "layout";
import { Status } from "constants/enum";
import { useAuth } from "providers/authProvider";
import { setDataAlert } from "store/slice/common.slice";
import { useMapScreenName } from "routes/mapScreenName";
import { setGroupCodeNames, showLoading } from "store/slice/common.slice";
import { setMenuDetails, setTargerScreen, setFirstRoutePath } from "store/slice/titleRoute.slice";
import { ICodename, IRecordMenuDetail, IUserAuthorizationResponse } from "commons/interfaces";
import IdentitySystemSettingService from "services/identity/systemSetting.service";


import "react-date-range/dist/styles.css"; // main style file
import "react-date-range/dist/theme/default.css"; // theme css file

import "./classes/globals.css";
import "./classes/utilities.css";
import "@maysoft/circle-common-react/dist/index.css";
import "@maysoft/common-component-react/dist/index.css";
import StyledEngineProvider from "@mui/material/StyledEngineProvider";
import ThemeProvider from "@mui/material/styles/ThemeProvider";
import theme from "assets/theme";
import { MaterialUIControllerProvider } from "components/context";
import CssBaseline from "@mui/material/CssBaseline";

moment.tz.setDefault("Asia/Saigon"); 

function App() {
    const auth = useAuth();
    const dispatchRedux = useDispatch();

    const dataMapScreenName = useMapScreenName();
    const currentLanguage = Strings.getLanguage();
    const { getDataCommonCodename } = useDataCommon();

    const [renderKey, setRenderKey] = useState(0);

    const [subMenu, setSubMenu] = useState<IRecordMenuDetail[]>([]);
    const [mainMenu, setMainMenu] = useState<IRecordMenuDetail[]>([]);

    const user = useSelector((state: RootState) => state.user);
    const userAuthorization = useSelector((state: RootState) => state.user.userAuthorization);
    const targetScreen: string = useSelector((state: RootState) => state.titleRoute.targerScreen);

    const [createUserSetting, setCreateUserSetting] = useState<ICreateUserSetting>();

    useEffect(() => {
        moment.locale(currentLanguage);
        setLocale({
            // use constant translation keys for messages without values
            mixed: {
                required: "Trường dữ liệu bắt buộc",
            },
            string: {
                email: "Sai định dạng email",
            },
        });
    }, [currentLanguage]);

    useEffect(() => {

        const pathName = window.location.pathname;
        if (pathName !== Screens.LOGIN_REDIRECT && targetScreen !== `${pathName}${window.location.search}`) {
            dispatchRedux(setTargerScreen(`${pathName}${window.location.search}`));
        }

        const setKey = () => {
            setRenderKey(Date.now());
        };

        __EventEmitter.addListener(Constants.EventName.LANGUAGE_CHANGE, setKey);

        return () => {
            __EventEmitter.removeListener(Constants.EventName.LANGUAGE_CHANGE, setKey);
        };
    }, []);

    useEffect(() => {
        if (auth?.user == null) {
            return;
        } else {
            let newSubMenu: IRecordMenuDetail[] = [];
            let newMainMenu: IRecordMenuDetail[] = [];
            const arrResourceURI = [
                Constants.MenuResourceURI.REPORT,
                Constants.MenuResourceURI.SETTING,
                Constants.MenuResourceURI.STATISTIC,
                Constants.MenuResourceURI.SYNTHETIC,
                Constants.MenuResourceURI.MONITORING,
            ];

            for (const element of [...user?.userAuthorization?.menuDetails || []]) {
                if (arrResourceURI.includes(element.resourceURI)) {
                    newMainMenu.push(element);
                } else {
                    newSubMenu.push(element);
                }
            };

            /* HIỆN TRẠNG:
             *      Những user member đang có quyền get tổ chức, phòng ban, user phòng ban
             *      Những quyền này đang dính với menu con của màn hình cấu hình
             *      Khi thấy đc menu con thì cũng sẽ thấy đc menu cha
             *      Nên vô tình những user role member thấy đc màn hình cấu hình, chỉ owner/admin mới thấy đc
             */

            const isOwner = user?.userAuthorization?.userAuthorizationResponse?.some(authResponse => RoleHelpers.isOwner(authResponse.roleType));
            const isAdmin = user?.userAuthorization?.userAuthorizationResponse?.some(authResponse => RoleHelpers.isAdmin(authResponse.roleType));

            // Ko hiện menu cấu hình
            if (!isOwner && !isAdmin) {
                const settingMenu = newMainMenu.find(menu => menu.resourceURI === Constants.MenuResourceURI.SETTING);
                if (settingMenu) {
                    newMainMenu = newMainMenu.filter(menu => menu.resourceURI !== settingMenu.resourceURI);
                    newSubMenu = newSubMenu.filter(subMenu => !subMenu.gene.startsWith(settingMenu.gene));
                }
            }

            setSubMenu(newSubMenu);
            setMainMenu(newMainMenu);
            dispatchRedux(setMenuDetails(newSubMenu));

            //#region Set first route on enter index page
            const firstRouteItem = newMainMenu?.[0];

            if (firstRouteItem) {
                let redirectUrl = firstRouteItem.externalUrl;
                if (firstRouteItem.resourceURI === Constants.MenuResourceURI.SETTING) {
                    const arrSub = user?.userAuthorization?.menuDetails?.filter(el => (
                        !Helpers.isNullOrEmpty(el.externalUrl)
                        && el.gene.startsWith(firstRouteItem.gene)
                    )) ?? [];
        
                    redirectUrl = arrSub[0]?.externalUrl;
                } else {
                    redirectUrl = firstRouteItem.externalUrl;
                }
        
                if (!Helpers.isNullOrEmpty(redirectUrl)) {
                    dispatchRedux(setFirstRoutePath(redirectUrl));
                }
            }
            //#endregion
        }
    }, [auth.user, user?.userAuthorization?.menuDetails]);

    useEffect(() => {
        if (user?.workingOrganization) {
            (async () => {
                const groups = await getDataCommonCodename(user?.workingOrganization);
                dispatchRedux(setGroupCodeNames({ ...groups }));
            })();

            (async () => {
                try {
                    const result = await (new IdentitySystemSettingService()).getByCondition({ settingCode: "CREATE_USER", serviceCode: Constants.SERVICE_CODE });
                    if (result?.settingValue) {
                        setCreateUserSetting(JSON.parse(result.settingValue) as ICreateUserSetting);
                    }
                } catch {}
            })()
        }
    }, [user?.workingOrganization]);

    const valueCommonProvider = useMemo(() => {

        let location = "";
        let currency = "";
        let tenantCode = "";
        let idGroupDefault = "";
        let organizationType = 0;
        let itemUserAuthorizationResponse;

        if ((user?.userAuthorization?.userAuthorizationResponse ?? []).length > 0) {
            // Lấy role lớn nhất của user
            itemUserAuthorizationResponse = user?.userAuthorization?.userAuthorizationResponse
                ?.filter(r => r.roleLevel !== RoleLevel.Membership)
                ?.sort((a, b) => Number(a.roleLevel) - Number(b.roleLevel))[0];
        } else {
            const roleTypeDefault = RoleType.Normal | RoleType.Member;
            itemUserAuthorizationResponse = {
                roleCode: "",
                roleName: "Default",
                roleType: roleTypeDefault,
                roleLevel: RoleLevelNormal.Member,
            } as IUserAuthorizationResponse;
        }

        let listOrganizations: ICodename[] = [...user?.userInfo?.currentServiceOrganizations || []]?.map((e) => {
            if (e.id === user?.workingOrganization) {
                currency = e.currency;
                location = e.location;
                tenantCode = e.tenantCode;
                organizationType = e.type;
            };

            const orgName = Helpers.getDefaultValueMultiLanguage(e.name, currentLanguage, true);

            return {
                detail: e,
                code: e.id,
                name: orgName,
            } as ICodename;
        });

        let listGroups: ICodename[] = [...user?.userInfo?.groupUsers || []]?.map((item) => {
            if (item.organizationId === user?.workingOrganization) {
                idGroupDefault = item.groupId;
            };
            const groupName = Helpers.getDefaultValueMultiLanguage(item.groupName, currentLanguage, true);
            return {
                name: groupName,
                code: item.groupId,
                detail: {
                    gene: item.groupGene,
                    group: item.organizationId,
                },
            } as ICodename;
        });

        if (Helpers.isNullOrEmpty(currency)) {
            currency = user?.userInfo?.defaultCurrency;
        };

        let propsTemp: any = {
            id: user?.userInfo?.userProfile?.id,
            email: user?.userInfo?.userProfile?.email,
            gender: user?.userInfo?.userProfile?.gender,
            fullName: user?.userInfo?.userProfile?.fullName,
            birthDate: user?.userInfo?.userProfile?.birthDate,
            phoneNumber: user?.userInfo?.userProfile?.phoneNumber,
            status: user?.userInfo?.userProfile?.status || Status.Active,
        };

        let itemOrganizationUserProfile: IOrganizationUserProfile | undefined = [...user?.userInfo?.organizationUserProfiles || []].find((item) => item.organizationId === user?.workingOrganization);
        if (itemOrganizationUserProfile) {
            const fullName = `${itemOrganizationUserProfile?.lastName || ""} ${itemOrganizationUserProfile?.firstName || ""}`.trim();

            propsTemp = {
                fullName: fullName,
                id: itemOrganizationUserProfile?.id || "",
                gender: itemOrganizationUserProfile?.gender,
                email: itemOrganizationUserProfile?.email || "",
                lastName: itemOrganizationUserProfile?.lastName || "",
                firstName: itemOrganizationUserProfile?.firstName || "",
                birthDate: itemOrganizationUserProfile?.dateOfBirth || 0,
                phoneNumber: itemOrganizationUserProfile?.phoneNumber || "",
                employmentDate: itemOrganizationUserProfile?.employmentDate,
                idCardNo: itemOrganizationUserProfile?.idCardNo,
                idCardIssuedDate: itemOrganizationUserProfile?.idCardIssuedDate,
                idCardIssuedPlace: itemOrganizationUserProfile?.idCardIssuedPlace,
                socialInsuranceCode: itemOrganizationUserProfile?.socialInsuranceCode,
                passportNo: itemOrganizationUserProfile?.passportNo,
                nationality: itemOrganizationUserProfile?.nationality || "",
                passportExpiredDate: itemOrganizationUserProfile?.passportExpiredDate,
                passportIssuedPlace: itemOrganizationUserProfile?.passportIssuedPlace,

                status: itemOrganizationUserProfile?.activeStatus || Status.Active,
            };
        }

        const userProfile: IUserProfile = {
            organizationType,
            groupId: idGroupDefault,
            organizationId: user?.workingOrganization,

            roleCode: itemUserAuthorizationResponse?.roleCode || "",
            roleName: itemUserAuthorizationResponse?.roleName || "Default",
            roleType: itemUserAuthorizationResponse?.roleType,
            roleLevel: itemUserAuthorizationResponse?.roleLevel,

            userName: user?.userInfo?.userProfile?.userName || "",
            avatarId: user?.userInfo?.userProfile?.avatarId || "",
            avatarUrl: user?.userInfo?.userProfile?.avatarUrl || "",
            identityId: user?.userInfo?.userProfile?.identityId || "",

            ...propsTemp,

            currency,
            location,
        };

        return {
            tenantCode,
            listGroups,
            userProfile,
            listOrganizations,
        };

    }, [
        currentLanguage,
        user?.workingOrganization,
        user?.userInfo?.groupUsers,
        user?.userInfo?.userProfile,
        user?.userInfo?.defaultCurrency,
        user?.userInfo?.organizationUserProfiles,
        user?.userInfo?.currentServiceOrganizations,
        user?.userAuthorization?.userAuthorizationResponse,
    ]);

    const renderRoute = useMemo(() => {
        const arrRoute: any[] = [];

        [...mainMenu || [], ...subMenu || []].forEach((item, key) => {
            arrRoute.push(<Route key={key} path={item.externalUrl} element={dataMapScreenName[item.screenName] ?? <></>} />);

            if (!Helpers.isNullOrEmpty(item.extraInformation)) {
                const extraInformation = JSON.parse(item.extraInformation);
                if (Array.isArray(extraInformation)) {
                    extraInformation.forEach((el) => {
                        arrRoute.push(
                            <Route
                                key={key}
                                path={el.ScreenPath || el.screenPath}
                                element={dataMapScreenName[el.ScreenName || el.screenName] ?? <></>}
                            />
                        );
                    });
                } else {
                    arrRoute.push(
                        <Route
                            key={key}
                            path={extraInformation?.ScreenPath || extraInformation?.screenPath}
                            element={dataMapScreenName[extraInformation?.ScreenName || extraInformation?.screenName] ?? <></>}
                        />
                    );
                }
            }
        });

        return arrRoute;
    }, [dataMapScreenName, mainMenu, subMenu]);

    const renderMainLayout = useMemo(() => {
        return (
            <MainLayout
                accessible={true}
                mainMenu={mainMenu}
            />
        )
    }, [mainMenu]);

    return (
        <CommonComponentProvider
            value={{
                axios: axios as any,
                userInfo: {
                    resourceCodes: [],
                    listGroup: valueCommonProvider?.listGroups,
                    userProfile: valueCommonProvider?.userProfile,
                    listOrganization: valueCommonProvider?.listOrganizations,
                },
                userAuthorization: {
                    userAuthorizationResponse: userAuthorization?.userAuthorizationResponse,
                },
                aesIV: process.env.REACT_APP_IV,
                aesKey: process.env.REACT_APP_SECRET_KEY,
                language: currentLanguage,
                listMultiLanguage: tabArrLanguage,
                dataMapScreenName: dataMapScreenName,

                clientId: Constants.CLIENT_ID,
                tenantCode: valueCommonProvider?.tenantCode,
                serviceCode: Constants.SERVICE_CODE,
                organizationId: user?.workingOrganization,

                createUserSetting,

                onShowLoading() {
                    dispatchRedux(showLoading(true));
                },
                onHideLoading() {
                    dispatchRedux(showLoading(false));
                },
                onSuccess(msg) {
                    dispatchRedux(setDataAlert({ message: msg, type: "success" }));
                },
                onError(msg, type) {
                    dispatchRedux(showLoading(false));
                    dispatchRedux(setDataAlert({ message: msg, type: type || "error" }));
                },
                onCallBack401() {
                    Helpers.showAlert(
                        Strings.MESSAGE.TOKEN_EXPIRED, "error",
                        async () => { await auth.signoutRedirect(); }
                    );
                },
                onCallBack403() {
                    Helpers.showAlert(
                        Strings.MESSAGE.NOT_PERMISSION, "error",
                        async () => { await auth.signoutRedirect(); }
                    );
                },
            }}
        >
            <CircleCommonProvider>
                <StyledEngineProvider injectFirst>
                    <ThemeProvider theme={theme}>
                        <MaterialUIControllerProvider>
                            <CssBaseline />
                            <ZaloBrowserRedirect />
                            <BrowserRouter key={renderKey}>
                                <Routes>
                                    <Route path={Screens.LOGIN_REDIRECT} element={<LoginRedirectScreen />} />
                                    <Route path={Screens.HOME} element={<ProtectedRoute>{renderMainLayout}</ProtectedRoute>}>
                                        <Route index element={<DashboardScreen />} />
                                        {renderRoute}
                                        <Route path="*" element={<NotFoundScreen />} />
                                    </Route>
                                </Routes>
                            </BrowserRouter>
                        </MaterialUIControllerProvider>
                    </ThemeProvider>
                </StyledEngineProvider>
            </CircleCommonProvider>
        </CommonComponentProvider>
    );
};

export default App;

const tabArrLanguage: ICodename[] = [
    { code: Constants.LanguageContent.VI, name: Strings.LANGUAGE.VIETNAMESE, },
    { code: Constants.LanguageContent.EN, name: Strings.LANGUAGE.ENGLISH, },
];
